// components/CheckoutForm.js
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import useCartStore, { MAX_QUANTITY } from "../../store/cartStore";
import OrderComplete from "../../components/ordercomplete/ordercomplete";
import useStatusStore from "../../store/statusStore";
import { CONTACT } from "../../config/location";
import {
    euro,
    Notice,
    QuantityStepper,
    SideDishSelect,
} from "../order/orderUi";

const MIN_DELIVERY_TOTAL = 30;

const Field = ({ label, className = "", ...props }) => (
    <label className={`field ${className}`}>
        {label}
        <input className="field-input !py-2.5 !text-base" {...props} />
    </label>
);

const ModeButton = ({ active, disabled, onClick, title, hint }) => (
    <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        aria-pressed={active}
        className={`border border-forest p-3.5 text-left text-sm font-semibold leading-[1.4] disabled:cursor-not-allowed disabled:opacity-40 ${
            active ? "bg-forest text-creme" : "bg-transparent text-forest"
        }`}
    >
        {title}
        <br />
        <span className="text-sm font-normal text-forest/70">{hint}</span>
    </button>
);

const CheckoutForm = () => {
    const [submitLoading, setSubmitLoading] = useState(false);
    const [error, setError] = useState(null);
    const cart = useCartStore((state) => state.cart);
    const totalPrice = useCartStore((state) => state.totalPrice)();
    const changeBeilage = useCartStore((state) => state.changeBeilage);

    const updateCustomerComment = useCartStore(
        (state) => state.updateCustomerComment
    );
    const removeItem = useCartStore((state) => state.removeItem);
    const increaseQuantity = useCartStore((state) => state.increaseQuantity);
    const decreaseQuantity = useCartStore((state) => state.decreaseQuantity);

    const [pickup, setPickup] = useState(true);
    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        email: "",
        phoneNumber: "",
        street: "",
        zipCode: "",
        city: "",
    });

    const [completedOrder, setCompletedOrder] = useState(null);

    const {
        deliveryAvailable,
        isInPickupTime,
        isInDeliveryTime,
        orderAvailable,
        fetchDeliveryAvailable,
        fetchOrderAvailable,
        fetchIsInDeliveryTime,
        fetchInPickupTime,
    } = useStatusStore();

    useEffect(() => {
        fetchDeliveryAvailable();
        fetchOrderAvailable();
        fetchIsInDeliveryTime();
        fetchInPickupTime();
    }, [fetchDeliveryAvailable, fetchOrderAvailable, fetchIsInDeliveryTime, fetchInPickupTime]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const belowMin = !pickup && totalPrice < MIN_DELIVERY_TOTAL;
    const deliveryDisabled = !isInDeliveryTime || !deliveryAvailable;
    const paymentNote = pickup ? "bar oder mit Karte" : "nur Barzahlung";

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        /* Check minimum value 30 Euro for delivey */
        if (belowMin) {
            setError(
                "Der Mindestbestellwert für Lieferungen beträgt 30 €. Bitte fügen Sie weitere Artikel hinzu."
            );
            return;
        }
        if (
            !formData.firstName ||
            !formData.lastName ||
            !formData.email ||
            !formData.phoneNumber
        ) {
            setError("Bitte füllen Sie alle Pflichtfelder aus.");
            return;
        }

        if (
            !pickup &&
            (!formData.city || !formData.zipCode || !formData.street)
        ) {
            setError(
                "Bitte geben Sie Ihre vollständige Adresse und Postleitzahl für die Lieferung ein."
            );
            return;
        }

        const orderData = {
            formData: formData,
            pickup: pickup,
            cart: cart,
        };

        setSubmitLoading(true);
        try {
            const response = await axios.post("/api/public/orders", orderData);
            // Clear the cart
            useCartStore.setState({ cart: [] });
            setCompletedOrder(response.data);
            // Ohne Animation, sonst bricht Safari (iPhone) beim Wechsel zur Bestätigung ab
            window.scrollTo({ top: 0, left: 0, behavior: "instant" });
        } catch (err) {
            //If statis code is 400, show the error message
            if (err.response && err.response.status === 400) {
                setError(err.response.data.message);
            } else {
                setError(
                    "Die Bestellung ist fehlgeschlagen. Bitte versuchen Sie es erneut."
                );
            }
        }
        setSubmitLoading(false);
    };

    if (completedOrder && completedOrder.totalPrice) {
        return <OrderComplete order={completedOrder} />;
    }

    return (
        <>
            <section className="sec page-head grid-2 !items-end pb-6">
                <Link to="/bestellen" className="eyebrow no-underline">
                    ← Zur Karte
                </Link>
                <h1 className="h1-page m-0 !text-[clamp(40px,5vw,72px)]">Kasse</h1>
            </section>

            {orderAvailable === false && (
                <div className="sec pb-10">
                    <Notice>
                        Bestellungen sind derzeit nicht möglich. Sie erreichen uns telefonisch unter{" "}
                        <a href={CONTACT.phoneHref} className="!text-messing-hell no-underline">
                            {CONTACT.phone}
                        </a>
                        .
                    </Notice>
                </div>
            )}

            {orderAvailable && cart.length === 0 && (
                <div className="sec pt-10 pb-[140px]">
                    <p className="body-text mb-8">Ihr Warenkorb ist leer.</p>
                    <Link to="/bestellen" className="btn btn-primary">
                        Zur Bestellkarte
                    </Link>
                </div>
            )}

            {orderAvailable && cart.length > 0 && (
                <form
                    onSubmit={handleSubmit}
                    noValidate
                    className="sec grid grid-cols-1 desk:grid-cols-2 items-start gap-[clamp(24px,4vw,64px)] pt-10 pb-[140px]"
                >
                    <div className="min-w-0">
                        <div className="eyebrow mb-4">Ihre Bestellung</div>
                        {cart.map((item) => (
                            <div key={item.id + item.beilage} className="border-b border-forest/10 py-[18px]">
                                <div className="flex justify-between gap-4 text-[17px]">
                                    <span>{item.title}</span>
                                    <span className="tabular-nums">{euro(item.price * item.quantity)}</span>
                                </div>
                                {item.description && (
                                    <div className="mt-0.5 text-sm text-forest/70">{item.description}</div>
                                )}
                                <div className="mt-3 flex flex-wrap items-center gap-3">
                                    <QuantityStepper
                                        size={40}
                                        max={MAX_QUANTITY}
                                        quantity={item.quantity}
                                        title={item.title}
                                        onDecrease={() => decreaseQuantity(item.id, item.beilage)}
                                        onIncrease={() => increaseQuantity(item.id, item.beilage)}
                                    />
                                    {item.beilage !== "" && (
                                        <SideDishSelect
                                            value={item.beilage}
                                            onChange={(value) => changeBeilage(item.id, item.beilage, value)}
                                            label={`Beilage für ${item.title}`}
                                            className="h-10 !py-0"
                                        />
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => removeItem(item.id, item.beilage)}
                                        className="h-10 bg-transparent text-sm text-forest/70 underline"
                                    >
                                        Entfernen
                                    </button>
                                </div>
                                <input
                                    value={item.customerComment}
                                    onChange={(e) => updateCustomerComment(item.id, item.beilage, e.target.value)}
                                    placeholder="Kommentar, z.B. ohne Zwiebeln"
                                    aria-label={`Kommentar zu ${item.title}`}
                                    className="field-input mt-3 !py-2.5 !text-[15px]"
                                />
                            </div>
                        ))}
                        <div className="flex justify-between py-5 font-caslon text-xl">
                            <span>Gesamt</span>
                            <span className="tabular-nums">{euro(totalPrice)}</span>
                        </div>
                        {belowMin && (
                            <div className="text-sm text-fehler">
                                Der Mindestbestellwert für Lieferungen beträgt 30 €.
                            </div>
                        )}
                    </div>

                    <div className="min-w-0 bg-leinen p-6 desk:p-8">
                        <div className="eyebrow mb-4">Abholung oder Lieferung</div>
                        <div className="mb-7 grid grid-cols-2 gap-2.5">
                            <ModeButton
                                active={pickup}
                                disabled={!isInPickupTime}
                                onClick={() => setPickup(true)}
                                title="Abholung"
                                hint={isInPickupTime ? "Bar- oder Kartenzahlung" : "außerhalb der Abholzeiten"}
                            />
                            <ModeButton
                                active={!pickup}
                                disabled={deliveryDisabled}
                                onClick={() => setPickup(false)}
                                title="Lieferung"
                                hint={
                                    !deliveryAvailable
                                        ? "zurzeit nicht verfügbar"
                                        : !isInDeliveryTime
                                          ? "außerhalb der Lieferzeiten"
                                          : "ab 30 €, nur Barzahlung"
                                }
                            />
                        </div>
                        <div className="grid grid-cols-1 desk:grid-cols-2 gap-[18px]">
                            {!pickup && (
                                <>
                                    <Field
                                        label="Straße und Hausnummer"
                                        name="street"
                                        value={formData.street}
                                        onChange={handleInputChange}
                                        autoComplete="street-address"
                                        required
                                        className="col-span-full"
                                    />
                                    <Field
                                        label="Postleitzahl"
                                        name="zipCode"
                                        value={formData.zipCode}
                                        onChange={handleInputChange}
                                        autoComplete="postal-code"
                                        inputMode="numeric"
                                        required
                                    />
                                    <Field
                                        label="Ort"
                                        name="city"
                                        value={formData.city}
                                        onChange={handleInputChange}
                                        autoComplete="address-level2"
                                        required
                                    />
                                </>
                            )}
                            <Field
                                label="Vorname"
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleInputChange}
                                autoComplete="given-name"
                                required
                            />
                            <Field
                                label="Nachname"
                                name="lastName"
                                value={formData.lastName}
                                onChange={handleInputChange}
                                autoComplete="family-name"
                                required
                            />
                            <Field
                                label="E-Mail"
                                name="email"
                                type="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                autoComplete="email"
                                required
                            />
                            <Field
                                label="Telefon"
                                name="phoneNumber"
                                type="tel"
                                value={formData.phoneNumber}
                                onChange={handleInputChange}
                                autoComplete="tel"
                                required
                            />
                        </div>
                        <div className="mt-6 text-sm leading-[1.6] text-forest/70">
                            Bezahlung vor Ort: {paymentNote}. Sie erhalten eine Bestätigung per E-Mail, sobald wir die
                            Bestellung angenommen haben.
                        </div>
                        {error && (
                            <div role="alert" className="mt-5 text-sm text-fehler">
                                {error}
                            </div>
                        )}
                        <button
                            type="submit"
                            disabled={belowMin || submitLoading}
                            className="btn btn-primary mt-5 w-full py-[18px]"
                        >
                            {submitLoading ? "Wird gesendet …" : `Bestellung abschicken · ${euro(totalPrice)}`}
                        </button>
                    </div>
                </form>
            )}
        </>
    );
};

export default CheckoutForm;
